import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { getDailyMessage, getGreeting } from "@/lib/dailyMessages";
import { logger } from "@/lib/logger";

const DEFAULT_LOGO = "/assets/happy-retreats-logo.png";
const DEFAULT_LOGO_DARK = "/assets/happy-retreats-logo-dark.png";

export default function Login() {
  const { login, user, loading } = useAuth();
  const [mode, setMode] = useState("signin"); // "signin" | "signup" | "signup_success"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "", date_of_birth: "", phone: "", date_of_joining: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoSrcLight, setLogoSrcLight] = useState(DEFAULT_LOGO);       // for dark backgrounds (hero panel)
  const [logoSrcDark, setLogoSrcDark] = useState(DEFAULT_LOGO_DARK);    // for light backgrounds (mobile, signup card)
  const [hasCustomLogo, setHasCustomLogo] = useState(false);
  const navigate = useNavigate();

  // Compute once per mount — message changes at next page load on the next day
  const todays = useMemo(() => getDailyMessage(), []);
  const greeting = useMemo(() => getGreeting(), []);
  const dateLabel = useMemo(
    () => new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" }),
    []
  );

  // Fetch branding (public endpoint) so super-admin's custom logos show here too.
  // Hero panel (dark bg) prefers the light-text variant; right panel (white bg)
  // prefers the dark-text variant. Each falls back to the legacy logo_path, then
  // the bundled default for that surface.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/branding");
        const dark = data?.logo_path_dark || data?.logo_path;
        const light = data?.logo_path_light || data?.logo_path;
        if (light) setLogoSrcLight(`${API}/files?path=${encodeURIComponent(light)}`);
        if (dark) setLogoSrcDark(`${API}/files?path=${encodeURIComponent(dark)}`);
        if (data?.logo_path_dark || data?.logo_path_light || data?.logo_path) setHasCustomLogo(true);
      } catch (e) {
        logger.error("branding fetch failed", e);
      }
    })();
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/signup", {
        name: signupForm.name.trim(),
        email: signupForm.email.trim().toLowerCase(),
        password: signupForm.password,
        date_of_birth: signupForm.date_of_birth,
        phone: signupForm.phone.trim(),
        date_of_joining: signupForm.date_of_joining || null,
      });
      setMode("signup_success");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => {
    setErr("");
    setMode(m);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: warm, team-facing message — feels like the team's daily home */}
      <div className="hidden lg:flex relative bg-zinc-950 text-white p-12 flex-col justify-between overflow-hidden">
        {/* Subtle background glows for warmth (not "AI slop" gradients — solid bg + 2 muted radial dots) */}
        <div className="absolute -right-40 -top-40 w-[480px] h-[480px] rounded-full bg-amber-500/10 blur-3xl" aria-hidden />
        <div className="absolute -left-32 bottom-0 w-[420px] h-[420px] rounded-full bg-rose-500/5 blur-3xl" aria-hidden />

        {/* Logo */}
        <div className="relative z-10 flex justify-center pt-6" data-testid="hr-logo">
          <img
            src={logoSrcLight}
            alt="Happy Retreats — Celebration Getaways"
            className="h-64 xl:h-72 w-auto opacity-95"
            onError={(e) => { if (e.currentTarget.src !== DEFAULT_LOGO) e.currentTarget.src = DEFAULT_LOGO; }}
          />
        </div>

        {/* Daily team message */}
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-3 text-zinc-500 text-[11px] uppercase tracking-widest">
            <span>{greeting}, team</span>
            <span className="h-px w-8 bg-zinc-700" />
            <span>{dateLabel}</span>
          </div>
          <h1
            className="font-display text-4xl xl:text-5xl font-black tracking-tighter leading-[1.05] mt-4"
            data-testid="daily-title"
          >
            {todays.title}
          </h1>
          <p className="text-zinc-300 mt-5 text-lg leading-relaxed" data-testid="daily-body">
            {todays.body}
          </p>
        </div>

        {/* Footer signature */}
        <div className="relative z-10 text-[11px] text-zinc-500 uppercase tracking-widest flex items-center justify-between">
          <span>— from the management, with love</span>
          <span className="text-zinc-600">Happy Retreats</span>
        </div>
      </div>

      {/* Right: sign-in or sign-up */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-sm">
          {/* Compact logo for mobile (left panel hidden < lg) */}
          {/* When using the bundled default, we show the dark-text variant on white. */}
          {/* When the super-admin has uploaded a custom logo, we keep their version on a dark tile (safe default for any color logo). */}
          <div className="lg:hidden mb-8 flex justify-center">
            {hasCustomLogo ? (
              <img
                src={logoSrcDark}
                alt="Happy Retreats"
                className="h-32 w-auto"
                onError={(e) => { if (e.currentTarget.src !== DEFAULT_LOGO_DARK) e.currentTarget.src = DEFAULT_LOGO_DARK; }}
              />
            ) : (
              <img
                src={DEFAULT_LOGO_DARK}
                alt="Happy Retreats — Celebration Getaways"
                className="h-32 w-auto"
              />
            )}
          </div>

          {/* Tab toggle */}
          {mode !== "signup_success" && (
            <div className="inline-flex rounded-md border border-zinc-200 p-0.5 mb-6" role="tablist">
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold rounded ${mode === "signin" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
                data-testid="tab-signin"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold rounded ${mode === "signup" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
                data-testid="tab-signup"
              >
                Create account
              </button>
            </div>
          )}

          {mode === "signin" && (
            <form onSubmit={submit} data-testid="login-form">
              <span className="label-overline">Sign in</span>
              <h2 className="font-display text-3xl font-black tracking-tighter mt-2 mb-2">Welcome back.</h2>
              <p className="text-sm text-zinc-500 mb-8">Pick up where you left off.</p>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Email or Employee ID</Label>
                  <Input
                    id="email"
                    type="text"
                    autoComplete="username"
                    placeholder="you@example.com  or  ABC001"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 h-11"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-2 h-11"
                    data-testid="login-password-input"
                  />
                </div>
                {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800" data-testid="login-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                </Button>
                <p className="text-xs text-zinc-500 text-center pt-1">
                  New here?{" "}
                  <button type="button" onClick={() => switchMode("signup")} className="text-zinc-900 underline font-semibold" data-testid="link-to-signup">
                    Create an account
                  </button>
                </p>
              </div>
            </form>
          )}

          {mode === "signup" && (
            <form onSubmit={submitSignup} data-testid="signup-form">
              <span className="label-overline">Create account</span>
              <h2 className="font-display text-3xl font-black tracking-tighter mt-2 mb-2">Join the team.</h2>
              <p className="text-sm text-zinc-500 mb-6">Submit your details — an administrator will review and approve your account before you can sign in.</p>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Full name</Label>
                  <Input
                    value={signupForm.name}
                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                    required
                    minLength={2}
                    className="mt-2 h-11"
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Email</Label>
                  <Input
                    type="email"
                    value={signupForm.email}
                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                    required
                    className="mt-2 h-11"
                    data-testid="signup-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Password</Label>
                  <Input
                    type="password"
                    value={signupForm.password}
                    onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                    required
                    minLength={6}
                    className="mt-2 h-11"
                    data-testid="signup-password-input"
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">Use at least 6 characters.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Date of birth</Label>
                    <Input
                      type="date"
                      value={signupForm.date_of_birth}
                      onChange={(e) => setSignupForm({ ...signupForm, date_of_birth: e.target.value })}
                      required
                      max={new Date().toISOString().slice(0, 10)}
                      className="mt-2 h-11"
                      data-testid="signup-dob-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Phone</Label>
                    <Input
                      type="tel"
                      value={signupForm.phone}
                      onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value })}
                      required
                      placeholder="+91 98765 43210"
                      className="mt-2 h-11"
                      data-testid="signup-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Date of joining <span className="text-zinc-400 normal-case font-normal">(if applicable)</span></Label>
                  <Input
                    type="date"
                    value={signupForm.date_of_joining}
                    onChange={(e) => setSignupForm({ ...signupForm, date_of_joining: e.target.value })}
                    max={new Date().toISOString().slice(0, 10)}
                    className="mt-2 h-11"
                    data-testid="signup-doj-input"
                  />
                </div>
                {err && <div className="text-sm text-red-600" data-testid="signup-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800" data-testid="signup-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
                </Button>
                <p className="text-xs text-zinc-500 text-center pt-1">
                  Already have an account?{" "}
                  <button type="button" onClick={() => switchMode("signin")} className="text-zinc-900 underline font-semibold" data-testid="link-to-signin">
                    Sign in
                  </button>
                </p>
              </div>
            </form>
          )}

          {mode === "signup_success" && (
            <div data-testid="signup-success">
              <span className="label-overline">Submitted</span>
              <h2 className="font-display text-3xl font-black tracking-tighter mt-2 mb-3">You're on the list.</h2>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Thanks, {signupForm.name.split(" ")[0] || "friend"}. Your account is pending approval.
                You'll be able to sign in as soon as an administrator approves it — typically within a business day.
              </p>
              <Button onClick={() => { setSignupForm({ name: "", email: "", password: "", date_of_birth: "", phone: "", date_of_joining: "" }); switchMode("signin"); }} className="mt-6 h-11 bg-zinc-950 hover:bg-zinc-800" data-testid="signup-success-back">
                Back to sign in
              </Button>
            </div>
          )}

          {/* Mobile-only daily message — surfaces the warmth even on small screens */}
          <div className="lg:hidden mt-10 border-t border-zinc-100 pt-6">
            <span className="label-overline text-zinc-500">{greeting}, team</span>
            <h3 className="font-display text-xl font-black tracking-tight mt-2" data-testid="daily-title-mobile">
              {todays.title}
            </h3>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed" data-testid="daily-body-mobile">
              {todays.body}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
